import { useState, useCallback } from 'react';
import { PaneNode, DropZone, DragState, createTabId, createPaneId } from '../../types';

/**
 * ドロップゾーンを生成する（usePaneLayoutから移動）
 */
function generateDropZones(rootPane: PaneNode, draggedFromPaneId: string, rootBounds?: DOMRect): DropZone[] {
  const dropZones: DropZone[] = [];
  const defaultBounds = rootBounds || { x: 0, y: 0, width: 800, height: 600 } as DOMRect;
  
  function traverseNode(node: PaneNode, parentBounds?: { x: number; y: number; width: number; height: number }) {
    if (node.type === 'container') {
      // 実際のDOM要素から座標を取得（可能な場合）
      const containerElement = document.querySelector(`[data-container-id="${node.id}"]`);
      const actualBounds = containerElement?.getBoundingClientRect();
      
      const bounds = actualBounds || parentBounds || { 
        x: defaultBounds.x, 
        y: defaultBounds.y, 
        width: defaultBounds.width, 
        height: defaultBounds.height 
      };
      
      // 自分自身のペインでない場合は、マージゾーンを追加
      if (node.id !== draggedFromPaneId) {
        // マージゾーン（中央）
        dropZones.push({
          id: `merge-${node.id}`,
          type: 'merge',
          targetPaneId: node.id,
          bounds: {
            x: bounds.x + bounds.width * 0.3,
            y: bounds.y + bounds.height * 0.3,
            width: bounds.width * 0.4,
            height: bounds.height * 0.4
          }
        });
      }
      
      // すべてのタブコンテナに対して分割ゾーンを生成（自分自身も含む）
      const splitZoneSize = 60; // サイズを少し小さく
      const cornerMargin = 80;  // 四隅の重複を避けるためのマージン
      
      // 上分割（四隅を避ける）
      dropZones.push({
        id: `split-top-${node.id}`,
        type: 'split-top',
        targetPaneId: node.id,
        bounds: {
          x: bounds.x + cornerMargin,
          y: bounds.y,
          width: bounds.width - (cornerMargin * 2),
          height: splitZoneSize
        }
      });
      
      // 下分割（四隅を避ける）
      dropZones.push({
        id: `split-bottom-${node.id}`,
        type: 'split-bottom',
        targetPaneId: node.id,
        bounds: {
          x: bounds.x + cornerMargin,
          y: bounds.y + bounds.height - splitZoneSize,
          width: bounds.width - (cornerMargin * 2),
          height: splitZoneSize
        }
      });
      
      // 左分割（四隅を避ける）
      dropZones.push({
        id: `split-left-${node.id}`,
        type: 'split-left',
        targetPaneId: node.id,
        bounds: {
          x: bounds.x,
          y: bounds.y + cornerMargin,
          width: splitZoneSize,
          height: bounds.height - (cornerMargin * 2)
        }
      });
      
      // 右分割（四隅を避ける）
      dropZones.push({
        id: `split-right-${node.id}`,
        type: 'split-right',
        targetPaneId: node.id,
        bounds: {
          x: bounds.x + bounds.width - splitZoneSize,
          y: bounds.y + cornerMargin,
          width: splitZoneSize,
          height: bounds.height - (cornerMargin * 2)
        }
      });
    } else {
      // Split pane の場合は子ノードを再帰的に処理
      const bounds = parentBounds || { 
        x: defaultBounds.x, 
        y: defaultBounds.y, 
        width: defaultBounds.width, 
        height: defaultBounds.height 
      };
      
      if (node.direction === 'horizontal') {
        // 水平分割の場合
        const leftBounds = {
          x: bounds.x,
          y: bounds.y,
          width: bounds.width * node.splitRatio,
          height: bounds.height
        };
        const rightBounds = {
          x: bounds.x + leftBounds.width,
          y: bounds.y,
          width: bounds.width * (1 - node.splitRatio),
          height: bounds.height
        };
        
        traverseNode(node.leftChild, leftBounds);
        traverseNode(node.rightChild, rightBounds);
      } else {
        // 垂直分割の場合
        const topBounds = {
          x: bounds.x,
          y: bounds.y,
          width: bounds.width,
          height: bounds.height * node.splitRatio
        };
        const bottomBounds = {
          x: bounds.x,
          y: bounds.y + topBounds.height,
          width: bounds.width,
          height: bounds.height * (1 - node.splitRatio)
        };
        
        traverseNode(node.leftChild, topBounds);
        traverseNode(node.rightChild, bottomBounds);
      }
    }
  }
  
  traverseNode(rootPane);
  
  return dropZones;
}

/**
 * ドラッグオーバーレイの状態管理フック
 * ドラッグ状態とドロップゾーンの表示を管理する
 */
export const useDragOverlay = () => {
  const [dragState, setDragState] = useState<DragState | undefined>(undefined);


  // ドラッグ開始
  const startDrag = useCallback((tabId: string, fromContainerId: string, rootPane: PaneNode) => {
    // requestAnimationFrameを使用してレンダリングサイクルと同期
    requestAnimationFrame(() => {
      // 実際のコンテナ要素のbounding rectを取得
      const containerElement = document.querySelector(`[data-container-id="${fromContainerId}"]`);
      const containerRect = containerElement?.getBoundingClientRect();
      
      const dropZones = generateDropZones(rootPane, fromContainerId, containerRect);
      
      setDragState({
        draggedTabId: createTabId(tabId),
        draggedFromPaneId: createPaneId(fromContainerId),
        dropZones
      });
    });
  }, []);

  // ドラッグ終了
  const endDrag = useCallback(() => {
    setDragState(undefined);
  }, []);

  // ドラッグ中かどうかの判定
  const isDragging = dragState !== undefined;

  return {
    dragState,
    isDragging,
    startDrag,
    endDrag
  };
};
